// Copyright (c) Ping. All rights reserved.
//
// Background effects picker — Figma DS "Background effects / desktop" (889:15715).
// Panel 480px: header ("Effects" + close) over three line-tabs
// (Backgrounds / Filters / Effects) above a tile grid. The Backgrounds tab is
// fully functional — None / Light blur / Strong blur (shown as blurred-photo
// previews), three built-in backgrounds (instant from cache) and a custom image
// upload. Selection is persisted + restored next call (presetBackgrounds.ts).
// Filters / Effects are placeholders for now. Web-only (uses <img> for blur
// previews + the file <input>).

import { useRef, useState } from 'react';
import { Pressable, Text, View, type ViewStyle } from 'react-native';

import { Ban, Blend, LayoutGrid, Plus, Sparkles, X } from 'lucide-react-native';

import { backgroundSupported, type BgEffectKind } from '@calls/effects/backgroundController';
import {
  addCustomBackground,
  type CustomBackground,
  fileToBackgroundDataUrl,
  listCustomBackgrounds,
  PRESET_BACKGROUNDS,
  presetImageUrl,
  presetThumbUrl,
  removeCustomBackground,
} from '@calls/effects/presetBackgrounds';
import { useCallStore } from '@calls/store/callStore';

import { useCallIconColors } from './callTheme';

type Tab = 'backgrounds' | 'filters' | 'effects';

// Tile geometry — 4 columns inside the 480px panel.
const TILE = { width: 108, height: 84 } as const;

function tileRing(selected: boolean): ViewStyle {
  return selected
    ? { borderWidth: 2, borderColor: '#537EFF' }
    : { borderWidth: 1, borderColor: '#2F333B' };
}

export function BackgroundEffectsMenu({ onClose }: { onClose: () => void }): JSX.Element {
  const currentKind = useCallStore((s) => s.backgroundEffect);
  const currentImageId = useCallStore((s) => s.backgroundImageId);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [tab, setTab] = useState<Tab>('backgrounds');
  // Locally-cached custom backgrounds, newest first — re-pickable across calls.
  const [customs, setCustoms] = useState<CustomBackground[]>(() => listCustomBackgrounds());
  const supported = backgroundSupported();
  const ic = useCallIconColors();

  // A real preset photo used as the blurred preview for the Light/Strong tiles.
  const blurPreview = presetThumbUrl('office');

  const pickBlur = (kind: BgEffectKind) => void useCallStore.getState().setBackgroundEffect(kind);
  const pickPreset = (id: string) => {
    const url = presetImageUrl(id);
    if (url === null) return;
    void useCallStore.getState().setBackgroundEffect('image', { imageUrl: url, imageId: id });
  };
  const pickCustom = (c: CustomBackground) =>
    void useCallStore.getState().setBackgroundEffect('image', { imageUrl: c.dataUrl, imageId: c.id });
  const deleteCustom = (c: CustomBackground) => {
    removeCustomBackground(c.id);
    setCustoms(listCustomBackgrounds());
    // If the deleted background was active, fall back to no effect.
    if (currentKind === 'image' && currentImageId === c.id) {
      void useCallStore.getState().setBackgroundEffect('none');
    }
  };
  const onFile = (e: { target: HTMLInputElement }) => {
    const f = e.target.files?.[0];
    if (!f) return;
    void (async () => {
      try {
        const url = await fileToBackgroundDataUrl(f);
        // Cache it in the reusable library, then select it by its stable id.
        const id = addCustomBackground(url);
        setCustoms(listCustomBackgrounds());
        await useCallStore.getState().setBackgroundEffect('image', { imageUrl: url, imageId: id });
      } catch {
        /* unreadable image */
      }
    })();
    e.target.value = '';
  };

  const TabBtn = ({ id, label, Icon }: { id: Tab; label: string; Icon: typeof LayoutGrid }) => {
    const active = tab === id;
    return (
      <Pressable
        accessibilityLabel={label}
        onPress={() => setTab(id)}
        className="h-[28px] flex-row items-center gap-[4px] rounded-[8px] px-[8px]"
      >
        <Icon size={16} color={active ? ic.primary : ic.muted} />
        <Text
          className={`text-[14px] ${active ? 'font-bold text-[#15171C] dark:text-[#F5F6F8]' : 'text-[#6B7280] dark:text-[#B5BAC3]'}`}
        >
          {label}
        </Text>
      </Pressable>
    );
  };

  return (
    <View className="w-[480px] rounded-[12px] border border-[#E1E3E8] dark:border-[#2F333B] bg-[#F2F3F5] dark:bg-[#222429] p-[4px]">
      {/* Header */}
      <View className="gap-[8px] px-[8px] py-[4px]">
        <View className="h-[22px] flex-row items-center justify-between">
          <Text className="text-[16px] text-[#6B7280] dark:text-[#B5BAC3]">Effects</Text>
          <Pressable accessibilityLabel="Close effects" onPress={onClose} hitSlop={8}>
            <X size={20} color={ic.muted} />
          </Pressable>
        </View>
        <View className="h-[28px] flex-row items-center gap-[8px]">
          <TabBtn id="backgrounds" label="Backgrounds" Icon={LayoutGrid} />
          <TabBtn id="filters" label="Filters" Icon={Blend} />
          <TabBtn id="effects" label="Effects" Icon={Sparkles} />
        </View>
      </View>

      {/* Tile section */}
      <View className="rounded-[8px] bg-white dark:bg-[#15171C] p-[8px]">
        {!supported ? (
          <View className="items-center justify-center p-[24px]">
            <Text className="text-[14px] text-[#6B7280] dark:text-[#B5BAC3]">
              Background effects aren’t supported in this browser.
            </Text>
          </View>
        ) : tab !== 'backgrounds' ? (
          <View className="h-[176px] items-center justify-center">
            <Text className="text-[14px] text-[#6B7280]">Coming soon</Text>
          </View>
        ) : (
          <View className="flex-row flex-wrap gap-[8px]">
            {/* None */}
            <Pressable
              accessibilityLabel="None"
              onPress={() => pickBlur('none')}
              className="items-center justify-center rounded-[6px] bg-[#F2F3F5] dark:bg-[#222429]"
              style={{ ...TILE, ...tileRing(currentKind === 'none') }}
            >
              <View className="flex-row items-center gap-[4px]">
                <Ban size={16} color={ic.muted} />
                <Text className="text-[14px] text-[#6B7280] dark:text-[#B5BAC3]">None</Text>
              </View>
            </Pressable>

            {/* Light / Strong blur — blurred photo previews */}
            {(['blur', 'strong'] as const).map((kind) => (
              <Pressable
                key={kind}
                accessibilityLabel={kind === 'blur' ? 'Light Blur' : 'Strong Blur'}
                onPress={() => pickBlur(kind)}
                className="overflow-hidden rounded-[6px] bg-[#F2F3F5] dark:bg-[#1A1C22]"
                style={{ ...TILE, ...tileRing(currentKind === kind) }}
              >
                {blurPreview !== null ? (
                  <img
                    src={blurPreview}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: `blur(${kind === 'strong' ? 9 : 4}px)`,
                      transform: 'scale(1.2)',
                    }}
                  />
                ) : null}
                <View
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                  className="items-center justify-center"
                >
                  <Text className="text-[14px] font-medium text-white">
                    {kind === 'blur' ? 'Light Blur' : 'Strong Blur'}
                  </Text>
                </View>
              </Pressable>
            ))}

            {/* Preset background photos (real bundled assets) */}
            {PRESET_BACKGROUNDS.map((p) => {
              const uri = presetThumbUrl(p.id);
              const selected = currentKind === 'image' && currentImageId === p.id;
              return (
                <Pressable
                  key={p.id}
                  accessibilityLabel={p.label}
                  onPress={() => pickPreset(p.id)}
                  className="overflow-hidden rounded-[6px] bg-[#F2F3F5] dark:bg-[#1A1C22]"
                  style={{ ...TILE, ...tileRing(selected) }}
                >
                  {uri !== null ? (
                    <img src={uri} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : null}
                </Pressable>
              );
            })}

            {/* Custom backgrounds the user has added before (re-pickable +
                deletable). Persisted in localStorage so they survive reloads. */}
            {customs.map((c) => {
              const selected = currentKind === 'image' && currentImageId === c.id;
              return (
                <View key={c.id} className="relative">
                  <Pressable
                    accessibilityLabel="Custom background"
                    onPress={() => pickCustom(c)}
                    className="overflow-hidden rounded-[6px] bg-[#F2F3F5] dark:bg-[#1A1C22]"
                    style={{ ...TILE, ...tileRing(selected) }}
                  >
                    <img src={c.dataUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Pressable>
                  <Pressable
                    accessibilityLabel="Delete background"
                    onPress={() => deleteCustom(c)}
                    hitSlop={6}
                    className="absolute right-[4px] top-[4px] h-[20px] w-[20px] items-center justify-center rounded-full bg-black/60 active:opacity-80"
                  >
                    <X size={13} color="#F5F6F8" />
                  </Pressable>
                </View>
              );
            })}

            {/* Custom upload (dashed) */}
            <Pressable
              accessibilityLabel="Add background image"
              onPress={() => fileRef.current?.click()}
              className="items-center justify-center rounded-[6px] bg-[#F2F3F5] dark:bg-[#1A1C22]"
              style={{ ...TILE, borderWidth: 1, borderColor: '#2F333B', borderStyle: 'dashed' }}
            >
              <View className="flex-row items-center gap-[4px]">
                <Plus size={16} color={ic.primary} />
                <Text className="text-[14px] text-[#15171C] dark:text-[#F5F6F8]">Add</Text>
              </View>
            </Pressable>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
          </View>
        )}
      </View>
    </View>
  );
}
